/**
 * SSO environment config for Dev / Test (QA) / Prod hosts.
 *
 * Redirect targets:
 *   - Dev (`dev.hgaccountingsystem.com`) → in-app manual login (`/login`)
 *   - Test (`qa.hgaccountingsystem.com`) and localhost → PingFederate / Okta SSO
 *   - Prod (`hgaccountingsystem.com`, no `dev`/`qa` in the host) → PingFederate / Okta SSO
 *
 * Manual password login (`/login`) is enabled for Dev and Test only.
 * Production uses SSO exclusively via the root route.
 *
 * Override with `REACT_APP_SSO_ENV=development|qa|test|production` when needed
 * (e.g. local builds pointed at a remote API).
 */

export const SSO_ENV = {
  DEVELOPMENT: "development",
  QA: "qa",
  PRODUCTION: "production",
};

/** Shared Test / localhost PingFederate start-SSO URL. */
const TEST_SSO_LOGIN_URL =
  "https://qa.hgssonew.com/sso/sp/startSSO.ping?PartnerIdpId=http%3A%2F%2Fwww.okta.com%2Fexk1nnk08crLRELwY0x8";

/** Production PingFederate start-SSO URL (`hgaccountingsystem.com`). */
const PROD_SSO_LOGIN_URL =
  "https://hgssonew.com/sso/sp/startSSO.ping?PartnerIdpId=http%3A%2F%2Fwww.okta.com%2Fexk1nwjjpjgeXkfgO0x8";

const ENV_CONFIG = {
  [SSO_ENV.DEVELOPMENT]: {
    id: SSO_ENV.DEVELOPMENT,
    appHost: "dev.hgaccountingsystem.com",
    /** In-app password login. */
    ssoLoginUrl: "/login",
    manualLoginEnabled: true,
  },
  [SSO_ENV.QA]: {
    id: SSO_ENV.QA,
    appHost: "qa.hgaccountingsystem.com",
    ssoLoginUrl: TEST_SSO_LOGIN_URL,
    manualLoginEnabled: true,
  },
  [SSO_ENV.PRODUCTION]: {
    id: SSO_ENV.PRODUCTION,
    appHost: "hgaccountingsystem.com",
    ssoLoginUrl: PROD_SSO_LOGIN_URL,
    manualLoginEnabled: false,
  },
};

function hostnameLooksLocal(hostname) {
  return (
    !hostname ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local")
  );
}

function isInAppPath(url) {
  return Boolean(url) && url.startsWith("/") && !url.startsWith("//");
}

/**
 * Resolves the active SSO environment from env override, then hostname, then NODE_ENV.
 * Localhost / 127.0.0.1 follow Test so they start PingFederate SSO.
 * @returns {typeof SSO_ENV[keyof typeof SSO_ENV]}
 */
export function resolveSsoEnvironment() {
  const fromEnv = String(process.env.REACT_APP_SSO_ENV || "")
    .trim()
    .toLowerCase();
  if (fromEnv === "dev") {
    return SSO_ENV.DEVELOPMENT;
  }
  if (fromEnv === "test") {
    return SSO_ENV.QA;
  }
  if (
    fromEnv === SSO_ENV.DEVELOPMENT ||
    fromEnv === SSO_ENV.QA ||
    fromEnv === SSO_ENV.PRODUCTION
  ) {
    return fromEnv;
  }

  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (host === ENV_CONFIG[SSO_ENV.DEVELOPMENT].appHost) {
      return SSO_ENV.DEVELOPMENT;
    }
    if (host === ENV_CONFIG[SSO_ENV.QA].appHost) {
      return SSO_ENV.QA;
    }
    if (
      host === ENV_CONFIG[SSO_ENV.PRODUCTION].appHost ||
      host === `www.${ENV_CONFIG[SSO_ENV.PRODUCTION].appHost}`
    ) {
      return SSO_ENV.PRODUCTION;
    }
    if (hostnameLooksLocal(host)) {
      return SSO_ENV.QA;
    }
  }

  if (process.env.NODE_ENV === "production") {
    return SSO_ENV.PRODUCTION;
  }

  return SSO_ENV.QA;
}

export function getSsoEnvironmentConfig(env = resolveSsoEnvironment()) {
  return ENV_CONFIG[env] ?? ENV_CONFIG[SSO_ENV.QA];
}

/** Whether the React `/login` password page should be available. */
export function isManualLoginEnabled() {
  return Boolean(getSsoEnvironmentConfig().manualLoginEnabled);
}

/**
 * URL used to start or retry SSO (or Dev manual login).
 */
export function getSsoLoginUrl() {
  return getSsoEnvironmentConfig().ssoLoginUrl || "";
}

/** True when the login URL stays inside this SPA (Dev `/login`). */
export function isInAppSsoLoginUrl(url = getSsoLoginUrl()) {
  return isInAppPath(url);
}

/** In-app home after a successful SSO (or password) login. */
export function getPostLoginHomePath() {
  return "/dashboard";
}

/** SSO callback route that receives `jwtToken` / `errormsg` query params. */
export const SSO_LOGIN_ACTION_PATH = "/ssologin.action";

/** In-app route that displays SSO failures. */
export const SSO_ERROR_PATH = "/sso-error";

/**
 * Leave the SPA immediately for the environment start-SSO URL
 * (PingFederate on Test/localhost/Prod, `/login` on Dev).
 * Returns false when no URL is configured.
 */
export function redirectToSsoLogin() {
  if (typeof window === "undefined") {
    return false;
  }
  const url = getSsoLoginUrl();
  if (!url) {
    return false;
  }
  window.location.replace(url);
  return true;
}

/**
 * Restart SSO from the public root so SsoRedirect can send the user
 * to the environment start-SSO (or Dev `/login`) URL.
 * Prefer `retrySsoLogin` from `ssoSession.js` so cache and cookies are cleared first.
 */
export function restartSsoLogin() {
  if (typeof window === "undefined") {
    return;
  }
  if (!redirectToSsoLogin()) {
    window.location.replace("/");
  }
}
